import { Paginated } from "src/@types/paginated";
import { apiSlice } from "../http";
import {
  IAccountConciliation,
  IAccountConciliationUpdate,
  INsuAuditHistoryItem,
  INsuVarDetails,
} from "src/@types/deposit-conference";
import { Tags } from "../../enum/tags";
import { IFile } from "src/@types/file";
import {
  INsu,
  INsuPayrollDiscountParams,
  IUpdateNsuStatusRequest,
  IUpdateNsuStatusResponse,
} from "src/@types/nsu";

export const nsuInformations = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getNsu: builder.query<Paginated<IAccountConciliation>, any>({
      query: (filters) => {
        const params = new URLSearchParams(
          filters as unknown as Record<string, string>
        );

        return {
          url: `/nsu?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: [Tags.NSU],
    }),
    getNsuDocuments: builder.query<Paginated<IFile>, any>({
      query: (filters) => {
        const params = new URLSearchParams(
          filters as unknown as Record<string, string>
        );

        return {
          url: `/file/NSU?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: [Tags.NSU],
    }),
    publicUpdateBatchNsu: builder.mutation<void, IAccountConciliationUpdate[]>({
      query: (body) => ({
        url: `/public/nsu/`,
        method: "PUT",
        body,
      }),
      invalidatesTags: [Tags.NSU, Tags.CAN_CLOSE_CONCILIATION, Tags.CONCILIATION_HISTORY],
    }),
    updateNsu: builder.mutation<void, IAccountConciliationUpdate>({
      query: (body) => ({
        url: `/nsu/${body.id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: [Tags.NSU, Tags.CAN_CLOSE_CONCILIATION, Tags.CONCILIATION_HISTORY],
    }),
    publicGetNsu: builder.query<Paginated<IAccountConciliation>, any>({
      query: (filters) => {
        const params = new URLSearchParams(
          filters as unknown as Record<string, string>
        );

        return {
          url: `/public/nsu?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: [Tags.NSU],
    }),
    updateBatchNsu: builder.mutation<void, IAccountConciliationUpdate[]>({
      query: (body) => ({
        url: `/nsu/`,
        method: "PUT",
        body,
      }),
      invalidatesTags: [Tags.NSU, Tags.CAN_CLOSE_CONCILIATION, Tags.CONCILIATION_HISTORY],
    }),

    getNsuAuditHistory: builder.query<INsuAuditHistoryItem[], { nsuId: number }>({
      query: ({ nsuId }) => {
        return {
          url: `/nsu/history/${nsuId}`,
          method: "GET",
        };
      },
      providesTags: [
        Tags.NSU,
        Tags.STORE_CONCILIATION,
        Tags.CAN_CLOSE_CONCILIATION,
      ],
    }),

    fetchNsuVarDetails: builder.mutation<INsuVarDetails, { nsuId: number }>({
      query: ({ nsuId }) => {
        return {
          url: `/nsu/${nsuId}/var-details`,
          method: "POST",
        };
      },
      // Não invalida Tags.NSU: refetchar a lista remontaria a linha e fecharia a
      // modal de Mais informações. Em vez disso faz um patch cirúrgico no cache das
      // listas de NSU, então reabrir a modal já mostra o valor sem re-chamar o VAR.
      async onQueryStarted({ nsuId }, { dispatch, queryFulfilled, getState }) {
        try {
          const { data } = await queryFulfilled;
          const entries = nsuInformations.util.selectInvalidatedBy(getState(), [
            Tags.NSU,
          ]);
          entries
            .filter(
              entry =>
                entry.endpointName === "getNsu" ||
                entry.endpointName === "publicGetNsu"
            )
            .forEach(entry => {
              dispatch(
                nsuInformations.util.updateQueryData(
                  entry.endpointName as "getNsu",
                  entry.originalArgs,
                  draft => {
                    const row = draft.data.find(nsu => nsu.id === nsuId);
                    if (row) {
                      row.operator_name = data.operatorName;
                      row.var_last_updated_at = data.lastUpdatedAt;
                    }
                  }
                )
              );
            });
        } catch {
          // erro é tratado na própria modal; nada a atualizar no cache
        }
      },
    }),

    // Payroll Discount / Loss NSU endpoints
    getPayrollDiscountNsu: builder.query<
      Paginated<INsu>,
      INsuPayrollDiscountParams
    >({
      query: ({ search, conciliation_date_start, conciliation_date_end, store_code, analyst, status, type, resubmission, orderBy, perPage, meta }) => {
        const params = new URLSearchParams({
          search,
          page: meta.page.toString(),
          perPage: perPage.toString(),
        });

        if (conciliation_date_start) {
          params.append('conciliation_date_start', conciliation_date_start);
        }

        if (conciliation_date_end) {
          params.append('conciliation_date_end', conciliation_date_end);
        }

        if (store_code) {
          params.append('store_code', store_code);
        }

        if (analyst) {
          params.append('analyst', analyst);
        }

        if (status && status.length > 0) {
          params.append('status', status.join(','));
        }

        if (type) {
          params.append('type', type);
        }

        if (resubmission) {
          params.append('resubmission', 'true');
        }

        if (orderBy) {
          params.append("orderBy", orderBy);
        }

        return {
          url: `/nsu/payroll-discount?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: [Tags.NSU],
    }),

    updateNsuStatus: builder.mutation<
      IUpdateNsuStatusResponse,
      IUpdateNsuStatusRequest
    >({
      query: ({ id, status, reason }) => ({
        url: "/nsu/payroll-discount/status",
        method: "PUT",
        body: {
          id,
          status,
          reason,
        },
      }),
      invalidatesTags: [Tags.NSU, Tags.CONCILIATION_HISTORY],
    }),

    batchUpdateNsuStatus: builder.mutation<
      { success: INsu[]; failed: { id: number; error: string }[] },
      { items: { id: number; status: string; reason?: string }[] }
    >({
      query: ({ items }) => ({
        url: "/nsu/payroll-discount/status/batch",
        method: "PUT",
        body: { items },
      }),
      invalidatesTags: [Tags.NSU, Tags.CONCILIATION_HISTORY],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetNsuQuery,
  useGetNsuDocumentsQuery,
  useUpdateNsuMutation,
  useUpdateBatchNsuMutation,
  usePublicGetNsuQuery,
  useGetNsuAuditHistoryQuery,
  useFetchNsuVarDetailsMutation,
  usePublicUpdateBatchNsuMutation,
  useGetPayrollDiscountNsuQuery,
  useUpdateNsuStatusMutation,
  useBatchUpdateNsuStatusMutation,
} = nsuInformations;

export const {
  endpoints: {
    getNsu: { select: selectGetNsu },
    getNsuDocuments: { select: selectGetNsuDocuments },
    updateNsu: { select: selectUpdateNsu },
    updateBatchNsu: { select: selectUpdateBatchNsu },
  },
} = nsuInformations;
